<template>
  <div>
    <Head title="Online Customers" />
    <div
      v-if="customers.data.length === 0 && form.phoneNumber && show && filters.freshchatUserId"
      class="flex items-center justify-between mb-8 max-w-3xl bg-red-500 rounded">
      <div class="flex items-center">
        <svg class="flex-shrink-0 ml-4 mr-2 w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm1.41-1.41A8 8 0 1 0 15.66 4.34 8 8 0 0 0 4.34 15.66zm9.9-8.49L11.41 10l2.83 2.83-1.41 1.41L10 11.41l-2.83 2.83-1.41-1.41L8.59 10 5.76 7.17l1.41-1.41L10 8.59l2.83-2.83 1.41 1.41z" /></svg>
        <div class="py-4 text-white text-sm font-medium">No customer found with this phone number {{ form.phoneNumber }}</div>
      </div>
      <button type="button" class="group mr-2 p-2" @click="show = false">
        <svg class="block w-2 h-2 fill-red-800 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" width="235.908" height="235.908" viewBox="278.046 126.846 235.908 235.908"><path d="M506.784 134.017c-9.56-9.56-25.06-9.56-34.62 0L396 210.18l-76.164-76.164c-9.56-9.56-25.06-9.56-34.62 0-9.56 9.56-9.56 25.06 0 34.62L361.38 244.8l-76.164 76.165c-9.56 9.56-9.56 25.06 0 34.62 9.56 9.56 25.06 9.56 34.62 0L396 279.42l76.164 76.165c9.56 9.56 25.06 9.56 34.62 0 9.56-9.56 9.56-25.06 0-34.62L430.62 244.8l76.164-76.163c9.56-9.56 9.56-25.06 0-34.62z" /></svg>
      </button>
    </div>
    <div
      v-if="customers.data.length === 0 && !form.phoneNumber && show && filters.noCustomer"
      class="flex items-center justify-between mb-8 max-w-3xl bg-indigo-500 rounded">
      <div class="flex items-center">
        <svg class="flex-shrink-0 ml-4 mr-2 w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M12.432 0c1.34 0 2.01.912 2.01 1.957 0 1.305-1.164 2.512-2.679 2.512-1.269 0-2.009-.75-1.974-1.99C9.789 1.436 10.67 0 12.432 0zM8.309 20c-1.058 0-1.833-.652-1.093-3.524l1.214-5.092c.211-.814.246-1.141 0-1.141-.317 0-1.689.562-2.502 1.117l-.528-.88c2.572-2.186 5.531-3.467 6.801-3.467 1.057 0 1.233 1.273.705 3.23l-1.391 5.352c-.246.945-.141 1.271.106 1.271.317 0 1.357-.392 2.379-1.207l.6.814C12.098 19.02 9.365 20 8.309 20z"/></svg>
        <div class="py-4 text-white text-sm font-medium">Customer not found in Cradle. Please search by phone number</div>
      </div>
    </div>
    <h1 class="mb-8 text-3xl font-bold">Customers</h1>
    <div class="flex items-center justify-between mb-6">
      <search-with-dropdown-filter v-show="!filters.noCustomer" v-model="form.search" :placeholderName="form.filterBy" class="mr-4 w-full max-w-md">
        <label class="block text-gray-700">Search Options:</label>
        <select v-model="form.filterBy" class="form-select mt-1 w-full">
          <option :value="null">-Select-</option>
          <option>Freshworks User ID</option>
          <option>Customer Name</option>
          <option>Phone Number</option>
          <option>Email Address</option>
          <option>KRA PIN</option>
        </select>
      </search-with-dropdown-filter>
      <search-filter-right v-show="filters.noCustomer" v-model="form.phoneNumber" class="mr-4 w-full max-w-md" />
      <button class="btn-orange text-white-400 hover:text-white-600" @click="searchCustomers">Search</button>
      <a class="btn-orange" href="#" @click="openCreateCustomerPrompt">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Customer</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Freshworks User ID</th>
          <th class="pb-3 pt-3 px-3">Customer Name</th>
          <th class="pb-3 pt-3 px-3">Phone Number</th>
          <th class="pb-3 pt-3 px-3">Alternative Phone Number</th>
          <th class="pb-3 pt-3 px-3">Email Address</th>
          <th class="pb-3 pt-3 px-3">KRA PIN Number</th>
          <th class="pb-3 pt-3 px-3">City</th>
          <th class="pb-3 pt-3 px-3">Region</th>
          <th class="pb-3 pt-3 px-3" />
        </tr>
        <tr v-for="customer in customers.data" :key="customer.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.freshchat_user_id }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.first_name }} {{ customer.last_name }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.phone_number }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.alternative_phone_number }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.email_address }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.kra_pin_number }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.city }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="url(customer)">
              {{ customer.region }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="url(customer)" tabindex="-1">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="customers.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="9">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="customers.links" />
  </div>
  <div v-if="openCreateCustomer" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6" style="display:inline !important;">
                <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
              Add Customer
            </h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form enctype="multipart/form-data" @submit.prevent="store">
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-2 pb-2">
                  <text-input v-model="formData.first_name" :error="$props.errors.first_name" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="First Name" />
                  <text-input v-model="formData.last_name" :error="$props.errors.last_name" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Last Name" />
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-2 pb-2">
                  <text-input v-model="formData.main_number" :error="$props.errors.main_number" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Main Phone Number" />
                  <text-input v-model="formData.alternative_number" :error="$props.errors.alternative_number" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Alternative Phone Number" />
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-2 pb-2">
                  <text-input v-model="formData.email_address" :error="$props.errors.email_address" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Email Address" />
                  <text-input v-model="formData.kra_pin_number" :error="$props.errors.kra_pin_number" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="KRA PIN Number" />
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-8 pb-8">
                  <p class="pb-1">Select Source</p>
                  <multiselect
                    id="source"
                    v-model="formData.source"
                    placeholder="Search Sources"
                    :options="sources"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="true"
                    class="mt-1 w-1/2"
                    :close-on-select="true"
                    :create-option="true"
                    :error="$props.errors.source"
                    :style="$props.errors.source ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                  />
                  <p v-if="$props.errors" class="text-red-700 pt-2"><small>{{ $props.errors.source }}</small></p>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-4 pl-2 pr-8 pb-8">
                  <p class="pb-1">Select City</p>
                  <multiselect
                    id="city"
                    v-model="formData.city"
                    placeholder="Search Cities"
                    :options="cities"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="true"
                    class="mt-1"
                    :close-on-select="true"
                    :create-option="true"
                    :error="$props.errors.city"
                    :style="$props.errors.city ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                  />
                  <p v-if="$props.errors" class="text-red-700 pt-2"><small>{{ $props.errors.city }}</small></p>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-4 pl-2 pr-2 pb-10">
                  <text-input v-model="formData.physical_address" :error="$props.errors.physical_address" class="pb-8 pr-6 w-full lg:w-full" label="Physical Address" />
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="store">
                Save
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilterRight from '@/Shared/SearchFilterRight'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import Multiselect from '@vueform/multiselect'
import SearchWithDropdownFilter from "@/Shared/SearchWithDropdownFilter";
  
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilterRight,
    SearchFilter,
    Pagination,
    TextInput,
    Multiselect,
    SearchWithDropdownFilter
  },
  layout: Layout,
  props: {
    filters: Object,
    customers: Object,
    sources: Object,
    cities: Object,
    errors: Object,
    freshchat_user_id: String,
    freshchat_conversation_id: String
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        phoneNumber: this.filters.phoneNumber,
        freshchat_conversation_id: this.filters.freshchat_conversation_id,
        filterBy: this.filters.filterBy
      },
      openCreateCustomer: false,
      formData: {
        'first_name': null,
        'last_name': null,
        'main_number': null,
        'alternative_number': null,
        'email_address': null,
        'kra_pin_number': null,
        'physical_address': null,
        'source': null,
        'city': null,
        'freshchat_user_id': this.freshchat_user_id,
        'freshchat_conversation_id': this.freshchat_conversation_id,
        'customer_type': null,
      },
      show: true,
    }
  },
  // watch: {
  //   form: {
  //     deep: true,
  //     handler: throttle(function () {
  //       this.$inertia.get('/online-customers', pickBy(this.form), { preserveState: true })
  //     }, 150),
  //   },
  // },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    searchCustomers() {
      this.$inertia.get('/online-customers', pickBy(this.form), { preserveState: true })
    },
    openCreateCustomerPrompt() {
      this.openCreateCustomer = true
    },
    closeModal() {
      this.openCreateCustomer = false
    },
    store() {
      this.formData._method = 'post'
      this.$inertia.post('/online-customers', this.formData, {
        onSuccess: () => {
          this.openCreateCustomer = false
        },
      })
    },
    url(customer) {
      let url = `online-customers/${customer.id}/edit`
      if (this.freshchat_user_id && this.freshchat_conversation_id) {
        url = `online-customers/${customer.id}/edit?freshchat_user_id=${this.freshchat_user_id}&freshchat_conversation_id=${this.freshchat_conversation_id}`
      }
      return url
    },
  },
}
</script>
<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
  